import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "../../lib/utils"
import { getFloatingSurfaceTheme, type FloatingSurface } from "./floating-surface"
import { useOverlayOwner, useScopedOverlayState } from "./overlay-scope"

const subscribeToPortal = () => () => {}
const clientPortalReady = () => true
const serverPortalReady = () => false

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const state = useScopedOverlayState(props)
  return <PopoverPrimitive.Root data-slot="popover" {...props} {...state} />
}

type PopoverTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean
}

function PopoverTrigger({
  ...props
}: PopoverTriggerProps) {
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      {...(props as React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>)}
    />
  )
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  surface = "floating",
  container,
  forceMount,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  container?: HTMLElement | null
  surface?: FloatingSurface
}) {
  const owner = useOverlayOwner()
  const portalReady = React.useSyncExternalStore(subscribeToPortal, clientPortalReady, serverPortalReady)
  const portalContainer = portalReady ? container || document.body : null

  // Don't render portal until we have a valid container
  if (!portalContainer || (owner && !owner.active)) {
    return null
  }

  return (
    <PopoverPrimitive.Portal container={portalContainer} forceMount={forceMount}>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        data-surface={surface}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[200] w-72 origin-(--radix-popover-content-transform-origin) rounded-lg border border-border/60 p-4 shadow-lg outline-hidden",
          getFloatingSurfaceTheme(surface),
          className
        )}
        {...props}
        data-overlay-owner={owner?.id}
      />
    </PopoverPrimitive.Portal>
  )
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

const PopoverClose = PopoverPrimitive.Close;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverClose }
